import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChallengeEvent, LeaderboardEntry, CompetitionTrophy, CompetitionNotification } from '../types/quest';
import type { CompetitionResult } from '../types/competition';
import { useChildStore } from './childStore';

const AGE_MULTIPLIERS: Record<string, number> = {
  'pre-k': 2.5,
  'kinder': 2.0,
  'early-elementary': 1.5,
  'upper-elementary': 1.0,
};

interface CompetitionState {
  events: ChallengeEvent[];
  results: CompetitionResult[];
  trophies: CompetitionTrophy[];
  notifications: CompetitionNotification[];
  lastSeenWeek: string;
  pendingCeremony: boolean;

  addEvent: (event: ChallengeEvent) => void;
  upsertEvent: (event: ChallengeEvent) => void;
  submitResult: (eventId: string, childId: string, score: number, questIds: string[]) => void;
  hasResult: (eventId: string, childId: string) => boolean;
  getLeaderboard: (eventId: string) => LeaderboardEntry[];
  getActiveEvents: () => ChallengeEvent[];
  getActiveEventsForQuiz: (quizType: string) => ChallengeEvent[];
  getResultsForChild: (childId: string) => CompetitionResult[];
  getTeamScore: (eventId: string) => number;

  setLastSeenWeek: (week: string) => void;
  awardTrophies: (weekId: string) => void;
  dismissCeremony: () => void;
  getTrophiesForChild: (childId: string) => CompetitionTrophy[];

  addNotification: (n: Omit<CompetitionNotification, 'id' | 'timestamp'>) => void;
  markNotificationsRead: (childId: string) => void;
  getNotificationsForChild: (childId: string) => CompetitionNotification[];
}

export const useCompetitionStore = create<CompetitionState>()(
  persist(
    (set, get) => ({
      events: [],
      results: [],
      trophies: [],
      notifications: [],
      lastSeenWeek: '',
      pendingCeremony: false,

      addEvent: (event) => {
        set(state => ({ events: [...state.events, event] }));
      },

      upsertEvent: (event) => {
        set(state => {
          if (state.events.some(e => e.id === event.id)) return state;
          return { events: [...state.events, event] };
        });
      },

      submitResult: (eventId, childId, score, questIds) => {
        const before = get().getLeaderboard(eventId);
        const myOldRank = before.find(e => e.childId === childId)?.rank ?? Infinity;

        set(state => ({
          results: [...state.results, {
            eventId, childId, score,
            completedQuests: questIds,
            submittedAt: new Date().toISOString(),
          }],
        }));

        const after = get().getLeaderboard(eventId);
        const myNew = after.find(e => e.childId === childId);
        if (!myNew || myNew.rank >= myOldRank) return;

        const myName = useChildStore.getState().children.find(c => c.id === childId)?.name ?? 'A sibling';
        for (const afterEntry of after) {
          if (afterEntry.childId === childId) continue;
          const beforeEntry = before.find(e => e.childId === afterEntry.childId);
          if (!beforeEntry) continue;
          if (afterEntry.rank > myNew.rank && beforeEntry.rank < myOldRank) {
            get().addNotification({
              childId: afterEntry.childId,
              fromChildId: childId,
              message: `${myName} just passed your score — only ${myNew.score - afterEntry.score} pts ahead!`,
              eventId,
              read: false,
            });
          }
        }
      },

      hasResult: (eventId, childId) => {
        return get().results.some(r => r.eventId === eventId && r.childId === childId);
      },

      getLeaderboard: (eventId) => {
        const children = useChildStore.getState().children;
        const childMap = new Map(children.map(c => [c.id, c]));

        const bestByChild = new Map<string, CompetitionResult>();
        for (const r of get().results) {
          if (r.eventId !== eventId) continue;
          const prev = bestByChild.get(r.childId);
          if (!prev || r.score > prev.score) bestByChild.set(r.childId, r);
        }

        return Array.from(bestByChild.values())
          .map(r => {
            const child = childMap.get(r.childId);
            const ageGroup = child?.ageGroup ?? 'upper-elementary';
            const multiplier = AGE_MULTIPLIERS[ageGroup] ?? 1.0;
            return { r, child, ageGroup, multiplier, adjusted: Math.round(r.score * multiplier) };
          })
          .sort((a, b) => b.adjusted - a.adjusted)
          .map(({ r, child, ageGroup, multiplier, adjusted }, i) => ({
            childId: r.childId,
            childName: child?.name ?? 'Unknown',
            avatarEmoji: child?.avatarEmoji ?? '🧒',
            score: adjusted,
            rawScore: r.score,
            multiplier,
            ageGroup,
            rank: i + 1,
            timestamp: r.submittedAt,
          } satisfies LeaderboardEntry));
      },

      getActiveEvents: () => {
        const now = new Date().toISOString();
        return get().events.filter(e => e.startDate <= now && e.endDate >= now);
      },

      getActiveEventsForQuiz: (quizType) => {
        return get().getActiveEvents().filter(e => e.quizTypes.includes(quizType));
      },

      getResultsForChild: (childId) => {
        return get().results.filter(r => r.childId === childId);
      },

      getTeamScore: (eventId) => {
        return get().getLeaderboard(eventId).reduce((sum, e) => sum + e.score, 0);
      },

      setLastSeenWeek: (week) => set({ lastSeenWeek: week }),

      awardTrophies: (weekId) => {
        const weekEvents = get().events.filter(e => e.id.endsWith(`-${weekId}`));
        const newTrophies: CompetitionTrophy[] = [];
        const existing = new Set(get().trophies.map(t => t.id));

        for (const event of weekEvents) {
          const leaderboard = get().getLeaderboard(event.id);
          for (const entry of leaderboard.slice(0, 3)) {
            const trophyId = `trophy-${event.id}-${entry.childId}`;
            if (existing.has(trophyId)) continue;
            newTrophies.push({
              id: trophyId,
              weekId,
              eventId: event.id,
              eventTitle: event.title,
              eventEmoji: event.emoji,
              childId: entry.childId,
              rank: entry.rank as 1 | 2 | 3,
              awardedAt: new Date().toISOString(),
            });
          }
        }

        if (newTrophies.length > 0) {
          set(state => ({
            trophies: [...state.trophies, ...newTrophies],
            pendingCeremony: true,
          }));
        }
      },

      dismissCeremony: () => set({ pendingCeremony: false }),

      getTrophiesForChild: (childId) => {
        return get().trophies.filter(t => t.childId === childId);
      },

      addNotification: (n) => {
        const notification: CompetitionNotification = {
          ...n,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
        };
        set(state => ({ notifications: [...state.notifications, notification] }));
      },

      markNotificationsRead: (childId) => {
        set(state => ({
          notifications: state.notifications.map(n =>
            n.childId === childId ? { ...n, read: true } : n
          ),
        }));
      },

      getNotificationsForChild: (childId) => {
        return get().notifications.filter(n => n.childId === childId);
      },
    }),
    { name: 'homeschool-competition', version: 0, migrate: (state) => state }
  )
);
